import { ApplicationLoggingAppName } from '@idu-nightlife/nightlife-utils';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { client } from '../../../../@api-client/client.gen';
import { useToastQueue } from '../../../feedback/app-error/use-toast-queue';
import { useAppMessaging } from '../../app-messaging/use-app-messaging';
import { useAuth } from '../../auth/hooks/useAuth';
import { createIDBPersister } from '../../auth/utils/create-index-db-persister';
import {
  createBrowserConnectivitySignal,
  createConnectivityController,
  type ConnectivityController,
  type ConnectivitySignal,
} from '../../offline/connectivity';
import { ForbiddenContext } from '../forbidden-context';
import { attachHttpStatus, refusalCodeFromError, shouldRetryQuery } from './api-error-status';
import { setApplicationContextHeader } from './application-context-header';
import { resolveCachePersistenceFailureAction } from './cache-persistence-failure';
import { shouldRecoverServerSession } from './server-session-recovery';
import { persistedQueryCacheBuster } from './persisted-query-cache-buster';
import { shouldPersistQuery } from './should-persist-query';
import { resolveLoggingData, useCachedAppVersionData } from './use-cached-app-version-data';

function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetryQuery,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
      },
    },
  });
}

const isIncidentReplayRequest = (request: Request | undefined) =>
  request?.url.includes('/incident/replay') ?? false;

// Provider
type ApiProviderProps = PropsWithChildren<{
  baseUrl: string;
  cacheKey?: string;
  appName: ApplicationLoggingAppName;
  connectivitySignal?: ConnectivitySignal;
  /**
   * Set by apps that keep working without a connection (Trackd), so a failure to
   * persist the react-query cache is surfaced to the operator. Leave it off for
   * online-only apps: there the cache is only a warm start, and the failure is
   * logged for us but never shown to the user.
   */
  offlineCapable?: boolean;
}>;

export const ApiProviderAuthenticated = ({
  children,
  baseUrl = '',
  cacheKey = 'reactQuery',
  appName,
  connectivitySignal,
  offlineCapable = false,
}: ApiProviderProps) => {
  const { logOut, getAccessToken, firebaseUser, isLoading } = useAuth();
  const { pushToast } = useToastQueue();
  const { mode, send } = useAppMessaging();
  const { venueSlug } = useParams();
  const [isForbidden, setIsForbidden] = useState(false);
  const getCachedAppVersionData = useCachedAppVersionData();

  // Fresh QueryClient per mount — each user session gets a clean, empty cache.
  // The component unmounts on logout (ProtectedLayout guards on !accessToken),
  // so stale data from a previous user can never bleed into the next session.
  const [queryClient] = useState(() => createQueryClient());

  const [interceptorsReady, setInterceptorsReady] = useState(false);
  const connectivityControllerRef = useRef<ConnectivityController | undefined>(undefined);

  // Clear both in-memory and persisted cache when a *different* user logs in
  // mid-session (e.g. token swap without a full unmount/remount cycle).
  const prevUidRef = useRef<string | undefined>(firebaseUser?.uid);
  useEffect(() => {
    const uid = firebaseUser?.uid;
    if (!uid) return;
    if (prevUidRef.current !== undefined && prevUidRef.current !== uid) {
      queryClient.clear();
      void createIDBPersister(cacheKey).removeClient();
    }
    prevUidRef.current = uid;
  }, [cacheKey, firebaseUser?.uid, queryClient]);

  const navigate = useNavigate();
  const forbiddenContextValue = useMemo(() => ({ isForbidden, setIsForbidden }), [isForbidden]);

  const hasReportedCacheFailureRef = useRef(false);
  const handleCachePersistenceFailure = useCallback(
    (error: unknown) => {
      const { log, toast } = resolveCachePersistenceFailureAction({
        offlineCapable,
        hasReported: hasReportedCacheFailureRef.current,
      });

      if (!log && !toast) return;
      hasReportedCacheFailureRef.current = true;

      if (log) {
        // Captured by the console interceptor in utils/logging and shipped to the
        // log sink. The error itself is what we need — the old handler dropped it,
        // leaving no way to tell a quota failure from a blocked-storage one.
        console.error(`[${appName}] React Query cache persistence failed`, error);
      }

      if (toast) {
        pushToast('Offline data could not be stored on this device.', 'error');
      }
    },
    [appName, offlineCapable, pushToast],
  );

  const persister = useMemo(
    () => createIDBPersister(cacheKey, { onError: handleCachePersistenceFailure }),
    [cacheKey, handleCachePersistenceFailure],
  );

  useEffect(() => {
    client.setConfig({ baseUrl });
  }, [baseUrl, navigate, pushToast, isLoading]);

  useEffect(() => {
    const controller = createConnectivityController({
      signal: connectivitySignal ?? createBrowserConnectivitySignal(),
    });
    connectivityControllerRef.current = controller;

    void controller.start();

    return () => {
      controller.stop();
      connectivityControllerRef.current = undefined;
    };
  }, [connectivitySignal]);

  useEffect(() => {
    const requestInterceptorId = client.interceptors.request.use(async (request) => {
      // The request-interceptor inactivity logout was removed as dead code: its
      // shouldRecoverServerSession gate could never fire for inactivity, and it
      // duplicated `useInactivityLogout` (auth.provider), which is the live control
      // and is intentionally disabled when offline sessions are enabled so an
      // offline operator is never stranded (FR-001). Replay still requires fresh auth.

      // Tell the parent shell that the user is active so it resets its own inactivity timer.
      if (mode === 'child') {
        send({ type: 'user-activity' });
      }

      if (venueSlug) {
        request.headers.set('x-venue-slug', venueSlug);
      }

      setApplicationContextHeader(request, appName);

      const token = request.headers.get('authorization') ?? getAccessToken();

      if (typeof token === 'string') {
        request.headers.set(
          'authorization',
          token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        );
      } else {
        request.headers.delete('authorization');
      }

      const appVersionData = await getCachedAppVersionData();
      request.headers.set(
        'x-logging-data',
        JSON.stringify(
          resolveLoggingData({
            appVersionData: appVersionData,
            appName,
          }),
        ),
      );

      return request;
    });

    const errorInterceptorId = client.interceptors.error.use(async (error, response, request) => {
      if (!response) {
        connectivityControllerRef.current?.recordRequestFailure();
      }

      attachHttpStatus(error, response?.status);

      // spec 026 FR-001/FR-016 — an unverified email is a 403, but it is NOT a permissions
      // problem: the person has every right to be here, they just have not proved their address
      // yet. Sending them to the Forbidden page would be a dead end (§XI forbids exactly that),
      // so this one 403 routes to the surface that can unblock them instead.
      //
      // Recognised by the machine-readable code the API carries in `error.cause`, not by matching
      // the message — copy changes, and a string comparison would silently stop working.
      if (response?.status === 403 && refusalCodeFromError(error) === 'EMAIL_NOT_VERIFIED') {
        navigate('/verify-email', { replace: true });
        return Promise.reject(error);
      }

      // Two-step sign-in turned on after this session began: the login page runs the verify step on the same session.
      if (response?.status === 403 && refusalCodeFromError(error) === 'TWO_STEP_REQUIRED') {
        navigate('/login', { replace: true });
        return Promise.reject(error);
      }

      // A refusal belongs to the venue its request was issued for. Switching venue leaves
      // requests in flight, and a late 403 for the venue the user has just left would blank
      // the page they are now on — the register they can legitimately see replaced by 403.
      const requestVenueSlug = request?.headers.get('x-venue-slug');
      const isStaleVenueRefusal = Boolean(requestVenueSlug) && requestVenueSlug !== venueSlug;

      if (response?.status === 403 && !isStaleVenueRefusal) {
        setIsForbidden(true);
      }

      if (
        response?.status === 401 &&
        shouldRecoverServerSession({
          isOnline: connectivityControllerRef.current?.getState() !== 'offline',
          isReplayRequest: isIncidentReplayRequest(request),
        })
      ) {
        await logOut();
        window.location.replace('/login');
      }

      // Handle global API errors (e.g., logging, notifications)
      return Promise.reject(error);
    });

    // A successful response confirms connectivity has genuinely returned, moving the
    // shared state out of 'reconnecting' back to 'online'. Without this the indicator
    // would stick on 'reconnecting' after any drop (only recordRequestFailure was wired).
    const responseInterceptorId = client.interceptors.response.use((response) => {
      if (response.ok) {
        connectivityControllerRef.current?.recordRequestSuccess();
      }
      return response;
    });

    setInterceptorsReady(true);

    return () => {
      client.interceptors.request.eject(requestInterceptorId);
      client.interceptors.error.eject(errorInterceptorId);
      client.interceptors.response.eject(responseInterceptorId);
      setInterceptorsReady(false);
    };
  }, [getAccessToken, navigate, logOut, venueSlug, mode, send, getCachedAppVersionData, appName]);

  // Avoid firing any API requests until auth has settled.
  // This is especially important for iframe child apps, which receive auth via postMessage.
  if (isLoading || !interceptorsReady) {
    return null;
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24, // 24 hours
        buster: persistedQueryCacheBuster(firebaseUser?.uid),
        dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
      }}
    >
      <ForbiddenContext.Provider value={forbiddenContextValue}>
        {children}
      </ForbiddenContext.Provider>
    </PersistQueryClientProvider>
  );
};
